import { useEffect, useState } from "react";
import { http, formatErr } from "../../lib/api";
import { usePolling } from "../../lib/usePolling";
import { useOpenParam } from "../../lib/useOpenParam";
import { formatDateIT } from "../../lib/labels";
import { toast } from "sonner";
import { Plus, UserCheck, Pencil } from "lucide-react";

const EMPTY = { commercial_name: "", company_name: "", phone: "", email: "", vat: "", address: "", notes: "", next_action: "", fiscal_code: "", sdi_code: "", pec: "", referente: "", source: "", website: "" };

export default function CrmContatti() {
  const [contacts, setContacts] = useState([]);
  const [agents, setAgents] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [showForm, setShowForm] = useState(false);
  const [busy, setBusy] = useState(false);
  const [sel, setSel] = useState(null);
  const [editing, setEditing] = useState(false);
  const [edit, setEdit] = useState(EMPTY);

  const load = async () => {
    const [c, a] = await Promise.all([http.get("/crm/contacts"), http.get("/crm/agents", { params: { status: "attivo" } })]);
    setContacts(c.data); setAgents(a.data);
  };
  useEffect(() => { load().catch(() => {}); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);
  usePolling(load, { interval: 10000, enabled: true });

  const on = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const onE = (k) => (e) => setEdit({ ...edit, [k]: e.target.value });

  const create = async (e) => {
    e.preventDefault(); setBusy(true);
    try { await http.post("/crm/contacts", form); setForm(EMPTY); setShowForm(false); await load(); toast.success("Contatto creato."); }
    catch (err) { toast.error(formatErr(err)); }
    setBusy(false);
  };

  const openContact = async (c) => {
    try { const { data } = await http.get(`/crm/contacts/detail/${c.id}`); setSel(data); setEdit({ ...EMPTY, ...data }); setEditing(false); }
    catch (e) { toast.error(formatErr(e)); }
  };
  useOpenParam(contacts, openContact);
  const saveEdit = async () => {
    setBusy(true);
    try { const { data } = await http.put(`/crm/contacts/detail/${sel.id}`, edit); setSel(data); setEditing(false); await load(); toast.success("Scheda cliente aggiornata."); }
    catch (e) { toast.error(formatErr(e)); }
    setBusy(false);
  };

  const assign = async (contact_id, agent_id) => {
    try { await http.post("/crm/contacts/assign", { contact_id, agent_id: agent_id || null }); await load(); if (sel?.id === contact_id) await openContact({ id: contact_id }); toast.success("Assegnazione aggiornata."); }
    catch (e) { toast.error(formatErr(e)); }
  };
  const agentName = (id) => { const a = agents.find((x) => x.id === id); return a ? `${a.profile?.first_name || a.email}` : null; };

  return (
    <div data-testid="crm-contatti-page" className="max-w-6xl mx-auto">
      <div className="flex items-center justify-between mb-5 gap-3 flex-wrap">
        <h1 className="font-serif text-2xl md:text-3xl">Contatti e clienti</h1>
        <button onClick={() => setShowForm((s) => !s)} data-testid="crm-contact-new" className="px-4 py-2 rounded-md text-white text-sm flex items-center gap-2 shrink-0" style={{ background: "var(--brand-dark)" }}><Plus className="w-4 h-4" /> Nuovo contatto</button>
      </div>

      {showForm && (
        <form onSubmit={create} className="card-clean p-5 mb-5 grid grid-cols-1 md:grid-cols-2 gap-3 text-sm" data-testid="crm-contact-form">
          <F label="Nome commerciale *" value={form.commercial_name} onChange={on("commercial_name")} testid="cc-name" required />
          <F label="Ragione sociale" value={form.company_name} onChange={on("company_name")} testid="cc-company" />
          <F label="Telefono" value={form.phone} onChange={on("phone")} testid="cc-phone" />
          <F label="Email" value={form.email} onChange={on("email")} testid="cc-email" />
          <F label="Partita IVA" value={form.vat} onChange={on("vat")} testid="cc-vat" />
          <F label="Indirizzo" value={form.address} onChange={on("address")} testid="cc-address" />
          <F label="Prossima azione" value={form.next_action} onChange={on("next_action")} testid="cc-next" />
          <F label="Provenienza" value={form.source} onChange={on("source")} testid="cc-source" />
          <div className="md:col-span-2"><F label="Note" value={form.notes} onChange={on("notes")} testid="cc-notes" /></div>
          <div className="md:col-span-2"><button type="submit" disabled={busy} data-testid="crm-contact-save" className="px-4 py-2 rounded-md text-white text-sm" style={{ background: "var(--brand-primary)" }}>Salva</button></div>
        </form>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        <div className="card-clean overflow-hidden lg:col-span-2" data-testid="crm-contacts-list">
          {contacts.length === 0 ? <div className="p-6 text-sm text-neutral-500">Nessun contatto.</div> : (
            <table className="w-full text-sm">
              <thead><tr className="text-left text-[10px] uppercase tracking-widest text-neutral-500 border-b" style={{ borderColor: "var(--border-silver)" }}>
                <th className="px-4 py-3">Nome</th><th className="px-4 py-3 hidden md:table-cell">P.IVA</th><th className="px-4 py-3">Gestione</th><th className="px-4 py-3">Assegna</th>
              </tr></thead>
              <tbody>
                {contacts.map((c) => (
                  <tr key={c.id} id={`open-${c.id}`} className={`border-b last:border-0 cursor-pointer hover:bg-neutral-50 ${sel?.id === c.id ? "bg-neutral-50" : ""}`} style={{ borderColor: "var(--border-silver)" }} data-testid={`crm-contact-row-${c.id}`} onClick={() => openContact(c)}>
                    <td className="px-4 py-3 font-medium underline decoration-dotted">{c.commercial_name}</td>
                    <td className="px-4 py-3 hidden md:table-cell text-neutral-500">{c.vat || "—"}</td>
                    <td className="px-4 py-3">{c.assigned_agent_id ? <span className="inline-flex items-center gap-1"><UserCheck className="w-3 h-3" />{agentName(c.assigned_agent_id) || "agente"}</span> : <span className="text-neutral-400">Non assegnato</span>}</td>
                    <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                      <select value={c.assigned_agent_id || ""} onChange={(e) => assign(c.id, e.target.value)} data-testid={`crm-assign-${c.id}`}
                        className="px-2 py-1 rounded border text-xs" style={{ borderColor: "var(--border-silver)" }}>
                        <option value="">— nessuno —</option>
                        {agents.map((a) => <option key={a.id} value={a.id}>{a.profile?.first_name || a.email}</option>)}
                      </select>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <div className="card-clean p-5" data-testid="crm-contact-detail">
          {!sel ? <p className="text-sm text-neutral-500">{contacts.length === 0 ? "Crea un contatto per iniziare." : "Seleziona un contatto per vederne la scheda."}</p> : editing ? (
            <div className="space-y-2 text-sm" data-testid="crm-contact-edit">
              <F label="Nome commerciale *" value={edit.commercial_name} onChange={onE("commercial_name")} testid="ce-name" />
              <F label="Ragione sociale" value={edit.company_name || ""} onChange={onE("company_name")} testid="ce-company" />
              <F label="Telefono" value={edit.phone || ""} onChange={onE("phone")} testid="ce-phone" />
              <F label="Email" value={edit.email || ""} onChange={onE("email")} testid="ce-email" />
              <F label="Partita IVA" value={edit.vat || ""} onChange={onE("vat")} testid="ce-vat" />
              <F label="Indirizzo" value={edit.address || ""} onChange={onE("address")} testid="ce-address" />
              <F label="Codice SDI" value={edit.sdi_code || ""} onChange={onE("sdi_code")} testid="ce-sdi" />
              <F label="PEC" value={edit.pec || ""} onChange={onE("pec")} testid="ce-pec" />
              <F label="Referente" value={edit.referente || ""} onChange={onE("referente")} testid="ce-referente" />
              <F label="Prossima azione" value={edit.next_action || ""} onChange={onE("next_action")} testid="ce-next" />
              <F label="Note" value={edit.notes || ""} onChange={onE("notes")} testid="ce-notes" />
              <div className="flex gap-2 pt-2">
                <button onClick={saveEdit} disabled={busy} data-testid="crm-contact-edit-save" className="px-4 py-2 rounded-md text-white text-sm" style={{ background: "var(--brand-primary)" }}>Salva</button>
                <button onClick={() => setEditing(false)} data-testid="crm-contact-edit-cancel" className="px-4 py-2 rounded-md border text-sm" style={{ borderColor: "var(--border-silver)" }}>Annulla</button>
              </div>
            </div>
          ) : (
            <div className="text-sm space-y-1.5" data-testid="crm-contact-view">
              <div className="flex items-center justify-between">
                <div className="font-serif text-lg" style={{ color: "var(--brand-dark)" }}>{sel.commercial_name}</div>
                <button onClick={() => { setEdit({ ...EMPTY, ...sel }); setEditing(true); }} data-testid="crm-contact-edit-open" className="text-xs flex items-center gap-1 px-2 py-1 rounded border" style={{ borderColor: "var(--border-silver)" }}><Pencil className="w-3 h-3" /> Modifica</button>
              </div>
              <Row k="Ragione sociale" v={sel.company_name} />
              <Row k="Telefono" v={sel.phone} />
              <Row k="Email" v={sel.email} />
              <Row k="Partita IVA" v={sel.vat} />
              <Row k="Indirizzo" v={sel.address} />
              <Row k="Codice SDI" v={sel.sdi_code} />
              <Row k="PEC" v={sel.pec} />
              <Row k="Referente" v={sel.referente} />
              <Row k="Provenienza" v={sel.source} />
              <Row k="Gestione" v={sel.assigned_agent_id ? (agentName(sel.assigned_agent_id) || "agente") : "Non assegnato"} />
              <Row k="Prossima azione" v={sel.next_action} />
              <Row k="Note" v={sel.notes} />
              <Row k="Creato" v={formatDateIT(sel.created_at)} />
              <Row k="Ultimo aggiornamento" v={formatDateIT(sel.updated_at)} />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function Row({ k, v }) {
  return <div className="flex gap-2"><span className="text-neutral-400 w-36 shrink-0">{k}</span><span className="flex-1">{v || "—"}</span></div>;
}

function F({ label, value, onChange, testid, required }) {
  return (
    <label className="block">
      <span className="text-[10px] uppercase tracking-widest text-neutral-500 block mb-1">{label}</span>
      <input value={value} onChange={onChange} required={required} data-testid={testid} className="w-full px-3 py-2 rounded-md border" style={{ borderColor: "var(--border-silver)" }} />
    </label>
  );
}
